import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { HELP_LIMITS, pageForScreen, type HelpDraft, type HelpPage, type HelpReply, type HelpWhere } from "@joogoos/contracts";
import { api } from "../api.js";
import { onScreen } from "../reports/onScreen.js";
import { ReportForm } from "../reports/ReportForm.js";

type Opens = {
  onOpenReport: (id: number) => void;
  /** A help page, by slug: the one an answer came from, or the one for this screen. */
  onOpenHelp?: ((slug: string) => void) | undefined;
  /** A place Help points to instead of answering: an account panel or a tab. */
  onOpenPlace?: ((where: HelpWhere) => void) | undefined;
};

/**
 * Help, opened from the Help panel of the account dialog (docs/19, ADR 0026): one box where a person asks how Joogoos
 * works, or tells us something is broken, missing or unclear. It was a button beside the tabs until 2026-09-21 — the
 * header is the balance and the account now, and the menu under the avatar stopped repeating the dialog's panels the
 * same day. What it does did not change, and it still carries the screen it was opened over.
 *
 * A question is answered from the help pages and names the page it came from; one about somebody's own charge or report
 * is pointed at where that lives; something broken or wanted becomes a report the person reads and sends themselves; and
 * anything else is declined in a sentence. Writing a report yourself is always one click away. Where asking is not
 * switched on — production, until the owner turns it on — Help is the report form and nothing else.
 */
export function HelpDialog({ onClose, onOpenReport, onOpenHelp, onOpenPlace }: Opens & { onClose: () => void }) {
  const status = useQuery({ queryKey: ["help"], queryFn: api.helpStatus, staleTime: Infinity });
  // Read once, when the dialog opens: that is the moment it has to be right.
  const [where] = useState(onScreen);
  // A question is often answered already: the page for this screen is one click away, before anything is typed.
  const guide = pageForScreen(where);
  const [chosen, setChosen] = useState<"ask" | "report" | null>(null);
  const [draft, setDraft] = useState<HelpDraft | null>(null);
  const available = status.data?.available === true;
  // Until the API says whether asking is on, only the frame is drawn. A status that cannot be read is the report form,
  // which always works.
  const mode = chosen ?? (status.isPending ? null : available ? "ask" : "report");

  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);

  const title = mode === "report" ? "Report" : "Help";
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-ink/35" onClick={onClose} />
      <div className="relative w-full max-w-[520px] max-h-[90vh] overflow-y-auto bg-page rounded-2xl shadow-[0_24px_70px_rgba(23,23,26,.35)] p-5 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="ml-auto text-faint hover:text-ink text-lg leading-none">×</button>
        </div>
        {mode === null ? <p className="text-[12.5px] text-muted">…</p>
          : mode === "ask" ? (
            <Ask
              where={where} guide={guide} turns={status.data?.turns ?? HELP_LIMITS.turns}
              onOpenHelp={onOpenHelp} onOpenPlace={onOpenPlace}
              onDraft={(d) => { setDraft(d); setChosen("report"); }}
              onReport={() => { setDraft(null); setChosen("report"); }}
            />
          ) : (
            <ReportForm
              // A new draft is a new form: what was typed into the last one belongs to the last one.
              key={draft ? `${draft.kind}:${draft.subject}` : "blank"}
              where={where} guide={guide} initial={draft}
              onClose={onClose} onOpenReport={onOpenReport} onOpenHelp={onOpenHelp}
              onAsk={available ? () => setChosen("ask") : undefined}
            />
          )}
      </div>
    </div>
  );
}

type Turn = { question: string; reply: HelpReply };

function Ask({ where, guide, turns, onOpenHelp, onOpenPlace, onDraft, onReport }: {
  where: ReturnType<typeof onScreen>;
  guide: HelpPage | null;
  turns: number;
  onOpenHelp?: ((slug: string) => void) | undefined;
  onOpenPlace?: ((where: HelpWhere) => void) | undefined;
  onDraft: (d: HelpDraft) => void;
  onReport: () => void;
}) {
  // The conversation is this dialog's and nobody else's: closed, it is gone, and nothing of it was kept (docs/19).
  const [done, setDone] = useState<Turn[]>([]);
  const [question, setQuestion] = useState("");
  const asking = useMutation({
    mutationFn: (q: string) => api.askHelp({
      question: q,
      earlier: done.map((t) => ({ question: t.question, answer: t.reply.text })),
      ...(where.screen ? { screen: where.screen } : {}),
      ...(where.step ? { step: where.step } : {}),
    }),
    onSuccess: (r, q) => { setDone((d) => [...d, { question: q, reply: r.reply }]); setQuestion(""); },
  });
  const full = done.length >= turns;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12.5px] text-muted leading-relaxed">
        Ask how something in Joogoos works. Answers come from the help pages, and Help cannot see your projects, your
        business page, your credit or your reports.
      </p>
      {guide ? (
        <p className="text-[12.5px] text-muted leading-relaxed -mt-1.5">
          How this screen works:{" "}
          {onOpenHelp
            ? <button type="button" onClick={() => onOpenHelp(guide.slug)} className="font-medium text-coral hover:text-coral-2">{guide.title}</button>
            : <b className="font-medium text-ink-2">{guide.title}</b>}
        </p>
      ) : null}

      {done.map((t, i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="ml-auto max-w-[85%] rounded-xl px-3 py-2 text-[13px] leading-relaxed bg-ink text-white">{t.question}</div>
          <Reply reply={t.reply} onOpenHelp={onOpenHelp} onOpenPlace={onOpenPlace} onDraft={onDraft} />
        </div>
      ))}

      {asking.error ? <p role="alert" className="text-[12px] text-bad">{String(asking.error)}</p> : null}

      {full ? (
        <p className="text-[12.5px] text-muted leading-relaxed">
          That is {turns} questions in this conversation. Write a report and a person will answer.
        </p>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); const q = question.trim(); if (q && !asking.isPending) asking.mutate(q); }} className="flex flex-col gap-2">
          <textarea
            aria-label="Your question" autoFocus value={question} onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.form?.requestSubmit(); } }}
            rows={2} maxLength={HELP_LIMITS.question} disabled={asking.isPending}
            placeholder="Does choosing a photo cost anything?"
            className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-[13px] resize-y disabled:opacity-60"
          />
          <div className="flex items-center gap-2">
            <button type="submit" disabled={!question.trim() || asking.isPending} className="px-3.5 py-1.5 rounded-lg bg-ink text-white text-xs font-medium disabled:opacity-60">
              {asking.isPending ? "Looking…" : "Ask"}
            </button>
            <span className="text-[11.5px] text-muted tabular-nums">{done.length} of {turns}</span>
          </div>
        </form>
      )}
      <button type="button" onClick={onReport} className="self-start px-0 py-1 text-xs font-medium text-ink-2 hover:text-ink underline underline-offset-2">
        Write a report instead
      </button>
    </div>
  );
}

/** One reply, drawn by what it is: an answer with its page, a place, a draft to read, or a decline. */
function Reply({ reply, onOpenHelp, onOpenPlace, onDraft }: {
  reply: HelpReply;
  onOpenHelp?: ((slug: string) => void) | undefined;
  onOpenPlace?: ((where: HelpWhere) => void) | undefined;
  onDraft: (d: HelpDraft) => void;
}) {
  const place = reply.action === "point_to" ? reply.place : reply.action === "decline" ? reply.place : null;
  const link = "self-start text-[12px] font-medium text-coral hover:text-coral-2";
  return (
    <div className="max-w-[90%] rounded-xl px-3 py-2 text-[13px] leading-relaxed bg-panel border border-line flex flex-col gap-1.5">
      <p>{reply.text}</p>
      {reply.action === "answer" ? (
        onOpenHelp
          ? <button type="button" onClick={() => onOpenHelp(reply.page.slug)} className={link}>From: {reply.page.title}</button>
          : <span className="text-[12px] text-muted">From: {reply.page.title}</span>
      ) : null}
      {place && onOpenPlace ? <button type="button" onClick={() => onOpenPlace(place.where)} className={link}>Open {place.label}</button> : null}
      {reply.action === "draft_report" ? <button type="button" onClick={() => onDraft(reply.draft)} className={link}>Read the report</button> : null}
      {reply.action === "decline" && reply.draft ? <button type="button" onClick={() => onDraft(reply.draft!)} className={link}>Send it to us as a question</button> : null}
    </div>
  );
}
